import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { Box, Flex, IconButton, Spinner, Tabs, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import useOnScreen from 'hooks/useOnScreen';
import { t } from 'i18next';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { ReactNode, useMemo, useRef } from 'react';
import slugify from 'slugify';

const SCROLL_AMOUNT_PX = 300;
const DEFAULT_GAP = '5';

interface ITabDef {
  value: string;
  label: string;
  labelIcon?: ReactNode;
  color?: RadixColor;
  content?: ReactNode;
  invisible?: boolean;
  disabled?: boolean;
  loading?: boolean;

  // shown on hover of the tab
  title?: string;
}

interface IProps {
  className?: string;
  gap?: string;

  // whether to draw the scrolling controls when screen is too narrow to display all tabs
  scroll?: boolean;

  scrollOptions?: {
    // how much of the first/last tab must be visible for it to count as "on screen" to hide/show the controls
    threshold?: number;
    // defaults to --color-background
    background?: '--color-background' | '--gray-2';
  };

  color?: RadixColor;

  tabs: ITabDef[];

  // provide this if parent needs to control which tab is shown
  value: string;
  onValueChange: (value: string) => void;

  contentClassName?: string;
}

export const CommonTabs = (props: IProps) => {
  const tabsWrapper = useRef<HTMLDivElement>(null);

  const { isVisible: isFirstVisible, ref: firstTab } = useOnScreen({
    threshold: props.scrollOptions?.threshold,
    // no need to watch for intersection if scroll is disabled
    disabled: !props.scroll,
    defaultIsVisible: true,
  });

  const { isVisible: isLastVisible, ref: lastTab } = useOnScreen({
    threshold: props.scrollOptions?.threshold,
    // no need to watch for intersection if scroll is disabled
    disabled: !props.scroll,
    defaultIsVisible: true,
  });

  const bgGradient = `var(${
    props.scrollOptions?.background ?? '--color-background'
  }), 75%, rgba(0,0,0,0)`;

  const visibleTabs = useMemo(
    () => props.tabs.filter((tab) => !tab.invisible),
    [props.tabs]
  );

  const activeTab = props.tabs.find((t) => t.value === props.value);

  return (
    <ErrorBoundary identifier="CommonTabs">
      <Box
        style={{
          width: '100%',
          position: 'relative',
        }}
        mb={props.gap || DEFAULT_GAP}
      >
        <div
          ref={tabsWrapper}
          // hide the native horizontal scrollbar (Radix already hides it on its
          // own tab list); tabs stay reachable via trackpad and the scroll arrows
          className="hide-scrollbar"
          style={{
            width: '100%',
            height: '52px',
            position: 'relative',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Tabs.Root
            className={props.className}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              // forces nav to take the full width if screen is wider than the tabs
              minWidth: '100%',
            }}
            value={props.value}
            onValueChange={(value) => {
              // let the parent handle it
              props.onValueChange(value);
            }}
          >
            <Tabs.List color={props.color}>
              {visibleTabs.map((tab, i) => (
                <Tabs.Trigger
                  key={`tab-${i}`}
                  ref={
                    i === 0
                      ? firstTab
                      : i === visibleTabs.length - 1
                        ? lastTab
                        : undefined
                  }
                  id={slugify(tab.value, { strict: true, lower: true })}
                  value={tab.value}
                  disabled={tab.disabled}
                  title={tab.title}
                >
                  {/* double-negation necessary because Spinner's loading defaults to true */}
                  <Spinner loading={!!tab.loading}>
                    <Flex gap={RADIX.FLEX.GAP.XS} align="center">
                      <Box>
                        <Text color={tab.color}>{t(tab.label)}</Text>
                      </Box>
                      {tab.labelIcon && (
                        <Flex align="center">{tab.labelIcon}</Flex>
                      )}
                    </Flex>
                  </Spinner>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>
        </div>

        {props.scroll && !isFirstVisible && (
          <Flex
            align="center"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              // to match nav bar height
              height: '44px',
              width: '100px',
              backgroundImage: `linear-gradient(to right, ${bgGradient})`,
            }}
          >
            <IconButton
              color={RADIX.COLOR.NEUTRAL}
              variant="soft"
              radius="full"
              onClick={() =>
                tabsWrapper.current?.scrollBy({
                  left: -SCROLL_AMOUNT_PX,
                  behavior: 'smooth',
                })
              }
            >
              <ArrowLeftIcon />
            </IconButton>
          </Flex>
        )}

        {props.scroll && !isLastVisible && (
          <Flex
            align="center"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              // to match nav bar height
              height: '44px',
              width: '100px',
              backgroundImage: `linear-gradient(to left, ${bgGradient})`,
            }}
          >
            <IconButton
              color={RADIX.COLOR.NEUTRAL}
              variant="soft"
              radius="full"
              style={{
                position: 'absolute',
                top: '4px',
                right: 0,
              }}
              onClick={() =>
                tabsWrapper.current?.scrollBy({
                  left: SCROLL_AMOUNT_PX,
                  behavior: 'smooth',
                })
              }
            >
              <ArrowRightIcon />
            </IconButton>
          </Flex>
        )}
      </Box>

      <Tabs.Root className={props.className} value={props.value}>
        <Tabs.Content value={props.value} className={props.contentClassName}>
          {!activeTab || activeTab.loading ? <Spinner /> : activeTab.content}
        </Tabs.Content>
      </Tabs.Root>
    </ErrorBoundary>
  );
};
